import React, { useCallback, useEffect, useState } from "react";
import { Alert, App, Button, Card, Input, Space, Tag, Typography } from "antd";
import { DeleteOutlined, GlobalOutlined, LinkOutlined } from "@ant-design/icons";
import {
  deleteAgentWhiteLabelDomainRequest,
  fetchAgentWhiteLabelDomain,
  submitAgentWhiteLabelDomain,
} from "../../../services/agentWhiteLabelDomainApi";
import {
  canAgentDeleteWhiteLabelDomainRequest,
  canAgentSubmitWhiteLabelDomain,
  whiteLabelDomainStatusColor,
  whiteLabelDomainStatusLabel,
} from "../../../utils/whiteLabelDomainDisplay";

const { Text, Paragraph } = Typography;

function errMessage(e) {
  const d = e?.response?.data;
  return d?.message || d?.error || e?.message || "שגיאה";
}

export default function AgentWhiteLabelDomainSettings() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [state, setState] = useState(null);
  const [draftDomain, setDraftDomain] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAgentWhiteLabelDomain();
      setState(data || null);
      setDraftDomain(data?.custom_domain || "");
    } catch (e) {
      message.error(errMessage(e));
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  const status = state?.custom_domain_status || "none";
  const canSubmit = canAgentSubmitWhiteLabelDomain(status);
  const canDelete = canAgentDeleteWhiteLabelDomainRequest(status);

  const handleSubmit = async () => {
    const domain = String(draftDomain || "").trim();
    if (!domain) {
      message.warning("יש להזין דומיין");
      return;
    }
    setSubmitting(true);
    try {
      const data = await submitAgentWhiteLabelDomain(domain);
      setState(data || null);
      setDraftDomain(data?.custom_domain || domain);
      message.success("הדומיין נשלח לאישור מנהל המערכת");
    } catch (e) {
      message.error(errMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const data = await deleteAgentWhiteLabelDomainRequest();
      setState(data || null);
      setDraftDomain("");
      message.success("בקשת הדומיין בוטלה");
    } catch (e) {
      message.error(errMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card loading={loading} size="small" title={<><GlobalOutlined /> דומיין מותאם / Custom Domain</>}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          חברו דומיין משלכם (למשל <Text code>your-agency.com</Text>) לאחר אימות DNS על ידי צוות HotelsRiver.
          עד לאישור, האתר הציבורי ימשיך להיפתח בכתובת ברירת המחדל של הפלטפורמה.
        </Paragraph>

        <Space wrap>
          <Text strong>סטטוס:</Text>
          <Tag color={whiteLabelDomainStatusColor(status)}>{whiteLabelDomainStatusLabel(status)}</Tag>
        </Space>

        {state?.custom_domain ? (
          <div>
            <Text strong>דומיין מבוקש: </Text>
            <Text copyable>{state.custom_domain}</Text>
          </div>
        ) : null}

        {state?.custom_domain_rejection_reason ? (
          <Alert
            type="warning"
            showIcon
            message="סיבת דחייה"
            description={state.custom_domain_rejection_reason}
          />
        ) : null}

        {state?.public_site_home_url ? (
          <div>
            <Text strong>כתובת אתר ציבורית: </Text>
            <a href={state.public_site_home_url} target="_blank" rel="noopener noreferrer">
              <LinkOutlined /> {state.public_site_home_url}
            </a>
            {status === "verified" ? (
              <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                לאחר אימות, זו הכתובת שתוצג ללקוחות ולכפתור &quot;צפה באתר שלי&quot;.
              </Paragraph>
            ) : null}
          </div>
        ) : null}

        {status === "pending" ? (
          <Alert
            type="info"
            showIcon
            message="הדומיין ממתין לאישור"
            description="לאחר שה-DNS מוגדר, צוות HotelsRiver יאמת את הדומיין. עד אז האתר נשאר בכתובת ברירת המחדל."
          />
        ) : null}

        {status === "verified" ? (
          <Alert
            type="success"
            showIcon
            message="הדומיין מאומת"
            description="לביטול או החלפת דומיין מאומת — פנו לצוות התמיכה / אדמין."
          />
        ) : null}

        {canSubmit ? (
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            <Input
              placeholder="www.example-travel.com"
              value={draftDomain}
              onChange={(e) => setDraftDomain(e.target.value)}
              disabled={submitting}
            />
            <Button type="primary" loading={submitting} onClick={handleSubmit}>
              שלח לאישור
            </Button>
          </Space>
        ) : null}

        {canDelete ? (
          <Button danger icon={<DeleteOutlined />} loading={deleting} onClick={handleDelete}>
            בטל בקשת דומיין
          </Button>
        ) : null}
      </Space>
    </Card>
  );
}
